import { useState, type FormEvent } from "react";
import { useRepriceBoq, type BoqDetail } from "../../api/boq";
import { formatMoney } from "../../api/money";

/** The approved quotation against the bill: K1 now and K1 quoted, and the
 * reprice that adopts the quotation in the same row. A quote above the kit
 * price needs the reason, which the server keeps in the override register; a
 * bill with the City says why it cannot be repriced yet. */
export default function RepricePanel({ detail }: { detail: BoqDetail }) {
  const reprice = useRepriceBoq(detail.id);
  const [reason, setReason] = useState("");
  const r = detail.reprice;
  if (r === null) {
    return null;
  }
  const pending = !r.applied && r.locked_reason === null;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    reprice.mutate(reason.trim() || null, { onSuccess: () => setReason("") });
  }

  return (
    <section className="cm-block" aria-label="Reprice from the quotation">
      <h3>Approved quotation {r.quotation.number}</h3>
      <p className="cm-doc-note">
        K1 on this bill: {formatMoney(r.k1_now)}. The quotation prices K1 at {formatMoney(r.k1_quoted)} and K2 at{" "}
        {formatMoney(r.k2_quoted)}.
      </p>
      {r.applied ? <p className="notice">This bill carries the quotation's prices.</p> : null}
      {r.locked_reason ? <p className="notice warn">{r.locked_reason}</p> : null}
      {pending ? (
        <form className="cm-form" aria-label={`Reprice from ${r.quotation.number}`} onSubmit={onSubmit}>
          {r.higher ? (
            <label className="move-note">
              Why the BOQ adopts the higher price (kept in the override register)
              <textarea value={reason} rows={2} onChange={(event) => setReason(event.target.value)} placeholder="Required" />
            </label>
          ) : null}
          <button
            type="submit"
            className="btn btn-primary"
            disabled={!detail.actions.can_reprice || reprice.isPending || (r.higher && reason.trim() === "")}
          >
            Reprice from {r.quotation.number}
          </button>
        </form>
      ) : null}
      {reprice.isError ? <p className="notice bad">{reprice.error.message}</p> : null}
    </section>
  );
}
